import * as React from 'react'
import { Image, Text, View, StyleSheet, Platform, Dimensions } from 'react-native'
import Formatter from '../../../../helpers/Formatter'
import QRCode from 'react-native-qrcode-svg'
import { useAppSelector } from '../../../../state/hooks'
import { add, sub, format, parseISO } from 'date-fns'

export function OrderConfirmedPageView({
	orderData,
	orderNumber,
	source,
	orgName,
	orgAddress,
	orgPhone,
	orderType,
	appType,
	webOrdersEnabled,
	orgURL,
	prices,
}) {
	const { restaurants } = useAppSelector(state => state.data)
	const { appDisplayOptions } = useAppSelector(state => state.settings)
	const { receipt } = appDisplayOptions
	let pickupDate = null
	if (orderData.isDelivery && orderData.cartPrep?.isScheduled) {
		pickupDate = sub(new Date(orderData.cartPrep?.deliveryTime), {
			minutes: orderData.cartPrep?.deliveryPrepTime - orderData.cartPrep?.longestPrepTime,
		})?.toISOString?.()
	} else if (orderData.cartPrep?.isScheduled) {
		pickupDate = orderData.cartPrep?.pickupTime
	} else {
		pickupDate = add(new Date(), { minutes: orderData?.cartPrep?.longestPrepTime })?.toISOString?.()
	}

	const formattedOrderDate = format(new Date(), 'M/d/yyyy, h:mm a')
	const formattedPickupDate = format(parseISO(pickupDate), 'M/d/yyyy, h:mm a')

	const getRestaurantName = (restaurantId: string) => {
		const restaurant = restaurants.find(r => r.id === restaurantId)
		return restaurant?.name
	}

	const groupedItems = Array.isArray(orderData.items)
		? Object.entries(
				orderData.items.reduce((acc, item) => {
					const restaurantId = item.rId
					if (!acc[restaurantId]) acc[restaurantId] = []
					acc[restaurantId].push(item)
					return acc
				}, {})
			)
		: null

	const priceReference = global.pos ? orderData.prices : prices

	return (
		<View style={styles.container}>
			<Image
				source={{ uri: source }}
				style={{ alignSelf: 'center', height: 200, width: 300 }}
				resizeMode="contain"
			/>
			<Text style={styles.title}>{orgName}</Text>
			<Text style={styles.centerText}>{orgAddress}</Text>
			<Text style={[styles.centerText, { marginBottom: 30 }]}>{orgPhone}</Text>
			<Text
				style={styles.centerText}
			>{`${orderType?.toUpperCase()} ORDER VIA ${appType?.toUpperCase()}`}</Text>
			<Text style={styles.centerText}>Order date: {formattedOrderDate}</Text>
			{orderData?.cartPrep?.pickupTime && (
				<Text style={styles.centerText}>Pickup time: {formattedPickupDate}</Text>
			)}

			<Text style={[styles.centerText, { marginTop: 40 }]}>
				{'ORDER #' + orderNumber + (orderData.takeOutName ? ' for ' + orderData.takeOutName : '')}
			</Text>
			{orderData?.selectedMethods?.phoneStringFormatted && (
				<Text style={[styles.centerText, { marginVertical: 20 }]}>
					{`Phone: ${orderData?.selectedMethods?.phoneStringFormatted}`}
				</Text>
			)}
			<Text style={[styles.centerText, { letterSpacing: 1, marginTop: 30 }]}>{'*'.repeat(50)}</Text>
			<View style={styles.objects}>
				{groupedItems &&
					groupedItems.map(([restaurantId, items]) => (
						<View key={restaurantId}>
							<Text style={{ fontSize: 24 }}>{getRestaurantName(restaurantId)}</Text>
							{(items as any[]).map((item, index) => (
								<View key={item.name + index} style={{ marginBottom: 10 }}>
									<View style={styles.itemContainer}>
										<Text style={styles.itemName}>{`${item.qty || 0}x ${item.name || ''}`}</Text>
										{item.total != null && item.total !== undefined && (
											<Text style={styles.itemPrice}>
												{Formatter.currencyFormat((item.ppu * item.qty || 0) / 100)}
											</Text>
										)}
									</View>
									{item?.mods?.length > 0 && (
										<View style={styles.modifiersContainer}>
											{item.mods.map((modifier, mIndex) => (
												<View key={mIndex + '_mod_' + modifier.name} style={styles.modifier}>
													<Text style={styles.modifierText}>+ {modifier.name}</Text>
													<Text style={styles.modifierText}>
														{Formatter.currencyFormat(
															(modifier.priceMoney.amount * item.qty || 0) / 100
														)}
													</Text>
												</View>
											))}
										</View>
									)}
								</View>
							))}
						</View>
					))}
				{priceReference?.subTotal !== 0 && (
					<View key={'star_subtotal'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Subtotal</Text>
						<Text style={styles.amounts}>
							{Formatter.currencyFormat(priceReference?.subTotal || 0)}
						</Text>
					</View>
				)}
				{priceReference?.tip !== 0 && (
					<View key={'star_tip'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Tip</Text>
						<Text style={styles.amounts}>{Formatter.currencyFormat(priceReference?.tip || 0)}</Text>
					</View>
				)}
				{priceReference?.serviceCharge !== 0 && (
					<View key={'star_sc'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Service Charge</Text>
						<Text style={styles.amounts}>
							{Formatter.currencyFormat(
								Math.round(priceReference?.serviceCharge * 100) / 100 +
									Math.round(priceReference?.serviceChargeGST * 100) / 100 +
									Math.round(priceReference?.serviceChargePST * 100) / 100
							)}
						</Text>
					</View>
				)}
				{priceReference?.bagFee + priceReference?.bagFeeTax !== 0 && (
					<View key={'star_bagfee'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Bag Fee</Text>
						<Text style={styles.amounts}>
							{Formatter.currencyFormat(
								(priceReference?.bagFee || 0) + (priceReference?.bagFeeTax || 0)
							)}
						</Text>
					</View>
				)}
				{priceReference?.discount !== 0 && (
					<View key={'star_discount'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Discount</Text>
						<Text style={styles.amounts}>
							{Formatter.currencyFormat(priceReference?.discount || 0)}
						</Text>
					</View>
				)}
				<View key={'star_taxes'} style={styles.itemContainer}>
					<Text style={styles.amounts}>Taxes</Text>
					<Text style={styles.amounts}>{Formatter.currencyFormat(priceReference?.taxes || 0)}</Text>
				</View>

				{priceReference?.total !== 0 && (
					<View key={'star_total'} style={styles.itemContainer}>
						<Text style={styles.amounts}>Total</Text>
						<Text style={styles.amounts}>
							{Formatter.currencyFormat(priceReference?.total || 0)}
						</Text>
					</View>
				)}
				<Text style={[styles.centerText, { letterSpacing: 1, marginTop: 30 }]}>
					{'*'.repeat(48)}
				</Text>
				{receipt.customText && (
					<View
						style={{
							width: '100%',
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'center',
							marginVertical: 40,
						}}
					>
						<Text style={[styles.centerText, { marginHorizontal: 10, marginBottom: 0 }]}>
							{receipt.customText}
						</Text>
					</View>
				)}
				{webOrdersEnabled && (
					<>
						<Text style={[styles.centerText, { marginBottom: 20 }]}>Order online:</Text>

						<View style={{ alignSelf: 'center' }}>
							<QRCode value={'https://' + orgURL} size={120} color="black" />
						</View>
						<Text style={styles.centerText}>{orgURL}</Text>
					</>
				)}

				<Text style={[styles.centerText, { marginVertical: 60 }]}>
					{'Thank you for your order!'}
				</Text>
			</View>
		</View>
	)
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		marginBottom: 100,
		width: 1000,
		marginLeft: -225,
	},
	objects: {
		marginHorizontal: 225,
		marginVertical: 10,
	},
	title: {
		fontSize: 36,
		textAlign: 'center',
		marginVertical: 40,
	},
	itemContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	itemName: {
		fontSize: 24,
		fontWeight: '300',
		paddingRight: 20,
	},
	itemPrice: {
		fontSize: 24,
		fontWeight: '300',
	},
	modifiersContainer: {
		marginTop: 5,
		marginLeft: 20,
	},
	modifier: {
		justifyContent: 'space-between',
		flexDirection: 'row',
	},
	modifierText: {
		fontSize: 22,
		color: '#555',
		fontWeight: '400',
	},
	amounts: {
		fontSize: 24,
		marginTop: 5,
		fontWeight: '300',
	},

	centerText: {
		fontSize: 24,
		textAlign: 'center',
		marginBottom: 10,
		fontWeight: '300',
	},
})
